import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence, LayoutGroup } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { techCategories, techMarqueeItems } from '../../data/techStack';

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const scroll = keyframes`
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
`;

const Section = styled.section`
  position: relative;
  padding: clamp(88px, 11vw, 120px) 0;
  overflow: hidden;
  background: radial-gradient(ellipse 70% 50% at 50% 0%, rgba(109, 187, 26, 0.06) 0%, transparent 55%),
    linear-gradient(180deg, #fff 0%, #f8fafb 50%, #f0faf4 100%);
`;

const Bg = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;

  .grid {
    position: absolute;
    inset: 0;
    background-image: linear-gradient(rgba(14, 107, 60, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(14, 107, 60, 0.035) 1px, transparent 1px);
    background-size: 48px 48px;
    mask-image: radial-gradient(ellipse 80% 60% at 50% 40%, black 0%, transparent 70%);
  }

  .orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    &.a {
      width: 360px;
      height: 360px;
      top: -10%;
      right: -8%;
      background: rgba(109, 187, 26, 0.15);
    }
    &.b {
      width: 280px;
      height: 280px;
      bottom: 0;
      left: -6%;
      background: rgba(14, 107, 60, 0.1);
    }
  }
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 var(--container-padding, 24px);
`;

const Header = styled(motion.header)`
  text-align: center;
  max-width: 680px;
  margin: 0 auto clamp(40px, 6vw, 56px);
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  margin-bottom: 18px;
  border-radius: 100px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98), rgba(240, 250, 244, 0.95));
  border: 1px solid rgba(14, 107, 60, 0.14);
  box-shadow: 0 4px 20px rgba(14, 107, 60, 0.08);
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: #0e6b3c;

  i {
    color: #6dbb1a;
  }
`;

const Title = styled.h2`
  font-family: var(--font-display);
  font-size: clamp(2rem, 4.8vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.12;
  color: #07111f;
  margin: 0 0 14px;

  span {
    background: linear-gradient(135deg, #0e6b3c 0%, #6dbb1a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Sub = styled.p`
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.75;
  color: #64748b;
`;

const MarqueeWrap = styled(motion.div)`
  margin-bottom: clamp(40px, 6vw, 56px);
  mask-image: linear-gradient(90deg, transparent, #000 6%, #000 94%, transparent);
`;

const MarqueeTrack = styled.div`
  display: flex;
  gap: 12px;
  width: max-content;
  animation: ${scroll} ${(p) => p.$dur || '42s'} linear infinite;
  animation-direction: ${(p) => (p.$reverse ? 'reverse' : 'normal')};
  padding: 6px 0;

  &:hover {
    animation-play-state: paused;
  }
`;

const MarqueePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 11px 18px 11px 14px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #e8eef3;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.06);
  white-space: nowrap;

  .ico {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(p) => `${p.$color}18`};
    border: 1px solid ${(p) => `${p.$color}30`};

    i {
      font-size: 1.15rem;
      color: ${(p) => p.$color};
    }
  }

  span {
    font-family: var(--font-display);
    font-weight: 600;
    font-size: 0.85rem;
    color: #334155;
  }
`;

const StackLayout = styled.div`
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: clamp(20px, 3vw, 32px);
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CategoryRail = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: sticky;
  top: calc(var(--nav-height, 72px) + 16px);

  @media (max-width: 900px) {
    position: static;
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 8px;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    gap: 10px;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const CatBtn = styled.button`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  padding: 16px 18px;
  border: 1px solid ${(p) => (p.$active ? 'rgba(14, 107, 60, 0.3)' : 'transparent')};
  border-radius: 18px;
  background: ${(p) => (p.$active ? 'linear-gradient(135deg, #f0faf4, #fff)' : 'transparent')};
  text-align: left;
  cursor: pointer;
  box-shadow: ${(p) => (p.$active ? '0 12px 32px rgba(14, 107, 60, 0.12)' : 'none')};
  transition: background 0.25s, border-color 0.25s;
  scroll-snap-align: start;
  flex-shrink: 0;

  @media (max-width: 900px) {
    min-width: 200px;
    flex-direction: column;
    gap: 8px;
    padding: 14px 16px;
  }

  &:hover:not([data-active='true']) {
    background: rgba(255, 255, 255, 0.8);
    border-color: #e8eef3;
  }
`;

const CatIcon = styled.div`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) =>
    p.$active ? 'linear-gradient(135deg, #0e6b3c, #6dbb1a)' : 'rgba(14, 107, 60, 0.08)'};
  color: ${(p) => (p.$active ? '#fff' : '#0e6b3c')};
  font-size: 1rem;
  transition: transform 0.3s;
`;

const CatText = styled.div`
  flex: 1;
  min-width: 0;

  strong {
    display: block;
    font-family: var(--font-display);
    font-size: 0.92rem;
    font-weight: 700;
    color: #0f172a;
    margin-bottom: 4px;
  }

  span {
    font-size: 0.75rem;
    color: #94a3b8;
    line-height: 1.4;
    display: block;
  }

  em {
    display: inline-block;
    margin-top: 8px;
    font-style: normal;
    font-size: 0.68rem;
    font-weight: 700;
    color: #0e6b3c;
    padding: 3px 8px;
    border-radius: 100px;
    background: rgba(14, 107, 60, 0.08);
  }
`;

const ActiveIndicator = styled(motion.div)`
  position: absolute;
  left: 0;
  top: 12px;
  bottom: 12px;
  width: 3px;
  border-radius: 0 4px 4px 0;
  background: linear-gradient(180deg, #6dbb1a, #0e6b3c);

  @media (max-width: 900px) {
    display: none;
  }
`;

const Panel = styled.div`
  border-radius: 24px;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(14, 107, 60, 0.35),
    rgba(109, 187, 26, 0.2),
    rgba(14, 107, 60, 0.1)
  );
  box-shadow: 0 24px 60px rgba(14, 107, 60, 0.1);
`;

const PanelInner = styled.div`
  border-radius: 23px;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(20px);
  padding: clamp(20px, 3vw, 28px);
  overflow: hidden;
`;

const PanelHead = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eef2f6;

  h3 {
    font-family: var(--font-display);
    font-size: clamp(1.25rem, 2.5vw, 1.5rem);
    font-weight: 800;
    color: #07111f;
    margin: 0 0 6px;
  }

  p {
    margin: 0;
    font-size: 0.88rem;
    color: #64748b;
    max-width: 400px;
    line-height: 1.55;
  }
`;

const PanelBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 100px;
  background: linear-gradient(135deg, rgba(14, 107, 60, 0.1), rgba(109, 187, 26, 0.12));
  border: 1px solid rgba(14, 107, 60, 0.15);
  font-size: 0.75rem;
  font-weight: 700;
  color: #0e6b3c;
  white-space: nowrap;

  i {
    animation: ${float} 3s ease infinite;
  }
`;

const TechGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
`;

const TechCard = styled(motion.article)`
  position: relative;
  border-radius: 16px;
  padding: 1px;
  background: linear-gradient(145deg, ${(p) => p.$color}40, rgba(14, 107, 60, 0.08));
  cursor: default;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(105deg, transparent 40%, rgba(255, 255, 255, 0.4) 50%, transparent 60%);
    background-size: 200% 100%;
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover::after {
    opacity: 1;
    animation: ${shimmer} 1.2s ease;
  }

  .card-inner {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 22px 12px 18px;
    border-radius: 15px;
    background: #fff;
    min-height: 118px;
    transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.35s;
  }

  &:hover .card-inner {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px ${(p) => `${p.$color}25`};
  }

  .icon-ring {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${(p) => `linear-gradient(145deg, ${p.$color}22, ${p.$color}08)`};
    border: 1px solid ${(p) => `${p.$color}35`};
    box-shadow: 0 4px 16px ${(p) => `${p.$color}20`};
    transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);

    i {
      font-size: 1.6rem;
      color: ${(p) => p.$color};
    }
  }

  &:hover .icon-ring {
    transform: scale(1.1) rotate(-4deg);
  }

  .name {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 0.82rem;
    color: #0f172a;
    text-align: center;
    line-height: 1.2;
  }

  .glow {
    position: absolute;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: ${(p) => p.$color};
    filter: blur(40px);
    opacity: 0;
    top: 20%;
    left: 50%;
    transform: translateX(-50%);
    transition: opacity 0.4s;
    pointer-events: none;
  }

  &:hover .glow {
    opacity: 0.2;
  }
`;

const Capabilities = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 14px;
  margin-top: clamp(40px, 6vw, 56px);

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const CapCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e8eef3;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05);
  transition: border-color 0.25s, transform 0.25s;

  &:hover {
    border-color: rgba(14, 107, 60, 0.25);
    transform: translateY(-3px);
  }

  .ico {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, rgba(14, 107, 60, 0.12), rgba(109, 187, 26, 0.15));
    color: #0e6b3c;
    font-size: 1rem;
    flex-shrink: 0;
  }

  div:last-child {
    strong {
      display: block;
      font-family: var(--font-display);
      font-size: 1.15rem;
      font-weight: 800;
      color: #0e6b3c;
      line-height: 1.1;
    }
    span {
      font-size: 0.72rem;
      color: #64748b;
    }
  }
`;

const categoryIcons = {
  frontend: 'fas fa-paint-brush',
  backend: 'fas fa-server',
  cms: 'fas fa-store',
  mobile: 'fas fa-cloud',
};

const capabilities = [
  { icon: 'fas fa-layer-group', value: '30+', label: 'Technologies mastered' },
  { icon: 'fab fa-react', value: 'React', label: 'Modern UI builds' },
  { icon: 'fab fa-laravel', value: 'Laravel', label: 'Enterprise backends' },
  { icon: 'fas fa-mobile-alt', value: '100%', label: 'Responsive delivery' },
];

function MarqueeContent({ items }) {
  const list = [...items, ...items];
  return list.map((t, i) => (
    <MarqueePill key={`${t.name}-${i}`} $color={t.color}>
      <div className="ico">
        <i className={t.icon} />
      </div>
      <span>{t.name}</span>
    </MarqueePill>
  ));
}

const TechStack = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.06 });
  const [activeId, setActiveId] = useState(techCategories[0].id);
  const active = techCategories.find((c) => c.id === activeId) || techCategories[0];

  const half = Math.ceil(techMarqueeItems.length / 2);
  const row1 = techMarqueeItems.slice(0, half);
  const row2 = techMarqueeItems.slice(half);

  return (
    <Section ref={ref} id="tech-stack">
      <Bg>
        <div className="grid" />
        <div className="orb a" />
        <div className="orb b" />
      </Bg>

      <Inner>
        <Header
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.55 }}
        >
          <Badge>
            <i className="fas fa-microchip" />
            Tech expertise
          </Badge>
          <Title>
            Modern Stack <span>We Use</span>
          </Title>
          <Sub>
            Cutting-edge tools for blazing-fast websites, smooth animations, e-commerce, mobile
            apps, and cloud-ready products.
          </Sub>
        </Header>

        <MarqueeWrap
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ delay: 0.1, duration: 0.5 }}
        >
          <MarqueeTrack $dur="40s" style={{ marginBottom: 10 }}>
            <MarqueeContent items={row1} />
          </MarqueeTrack>
          <MarqueeTrack $dur="48s" $reverse>
            <MarqueeContent items={row2} />
          </MarqueeTrack>
        </MarqueeWrap>

        <StackLayout>
          <CategoryRail aria-label="Technology categories">
            <LayoutGroup>
              {techCategories.map((c) => {
                const isActive = activeId === c.id;
                return (
                  <CatBtn
                    key={c.id}
                    type="button"
                    $active={isActive}
                    data-active={isActive}
                    onClick={() => setActiveId(c.id)}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    {isActive && (
                      <ActiveIndicator layoutId="tech-cat-indicator" transition={{ type: 'spring', stiffness: 500, damping: 32 }} />
                    )}
                    <CatIcon $active={isActive}>
                      <i className={categoryIcons[c.id] || 'fas fa-code'} />
                    </CatIcon>
                    <CatText>
                      <strong>{c.label}</strong>
                      <span>{c.description}</span>
                      <em>{c.items.length} tools</em>
                    </CatText>
                  </CatBtn>
                );
              })}
            </LayoutGroup>
          </CategoryRail>

          <Panel>
            <PanelInner>
              <AnimatePresence mode="wait">
                <PanelHead
                  key={active.id}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.25 }}
                >
                  <div>
                    <h3>{active.label}</h3>
                    <p>{active.description} — production-ready integrations for your project.</p>
                  </div>
                  <PanelBadge>
                    <i className="fas fa-check-circle" />
                    {active.items.length} technologies
                  </PanelBadge>
                </PanelHead>
              </AnimatePresence>

              <AnimatePresence mode="wait">
                <TechGrid
                  key={active.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {active.items.map((t, i) => (
                    <TechCard
                      key={t.name}
                      $color={t.color}
                      initial={{ opacity: 0, y: 20, scale: 0.94 }}
                      animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
                      transition={{
                        delay: i * 0.04,
                        type: 'spring',
                        stiffness: 280,
                        damping: 24,
                      }}
                    >
                      <div className="glow" />
                      <div className="card-inner">
                        <div className="icon-ring">
                          <i className={t.icon} />
                        </div>
                        <span className="name">{t.name}</span>
                      </div>
                    </TechCard>
                  ))}
                </TechGrid>
              </AnimatePresence>
            </PanelInner>
          </Panel>
        </StackLayout>

        <Capabilities
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          {capabilities.map((cap) => (
            <CapCard key={cap.label}>
              <div className="ico">
                <i className={cap.icon} />
              </div>
              <div>
                <strong>{cap.value}</strong>
                <span>{cap.label}</span>
              </div>
            </CapCard>
          ))}
        </Capabilities>
      </Inner>
    </Section>
  );
};

export default TechStack;
